import { getPriceWithCurrency } from '~/helpers/priceHelper'
import type { SupportedCurrencies } from 'lc-services/types'

type HousePreOrder = {
  bookingSent: string
  destinationId: string
  endDate: string
  externalIdentifier: string
  nightCount: string
  pid: string
  price: string
  startDate: string
  uuid: string
}

export const useHousePreOrder = () => {
  const AMOUNT_DEPOSIT = 500

  const { $api, $httpCookies, $i18n } = useNuxtApp()
  const housePreOrderState = useState<HousePreOrder>(
    'state-house-pre-order',
    () => ({
      bookingSent: '',
      destinationId: '',
      endDate: '',
      externalIdentifier: '',
      nightCount: '',
      pid: '',
      price: '',
      startDate: '',
      uuid: '',
    }),
  )

  const preOrder = computed(() => housePreOrderState.value)
  const deposit = computed(() => AMOUNT_DEPOSIT)

  const isEmpty = computed(() => {
    const { destinationId, endDate, nightCount, pid, price, startDate } =
      preOrder.value

    return Object.values({
      destinationId,
      endDate,
      nightCount,
      pid,
      price,
      startDate,
    }).includes('')
  })

  const fetchPriceCurrencyHouse = async ({
    houseId,
    houseCurrency,
    housePrivateToken,
  }: {
    houseId: string
    houseCurrency: SupportedCurrencies
    housePrivateToken?: string
  }) => {
    const periodParams = {
      currency: houseCurrency,
      end_at: preOrder.value.endDate,
      start_at: preOrder.value.startDate,
      private_token: housePrivateToken || '',
    }

    const selectedPeriod = await $api.v1.housePeriods
      .read(houseId, {
        params: periodParams,
      })
      .then((res: { data: { allMinimumDurationValid: boolean } }) => {
        if (res?.data) {
          const { data } = res
          const validPrice = data.allMinimumDurationValid
          return { validPrice, ...data }
        }
      })
      .catch((err: unknown) => console.warn(err))

    if (selectedPeriod?.validPrice && selectedPeriod?.publicPrice) {
      setPrice(selectedPeriod.publicPrice.toString())
    }
  }

  const formatPrice = (
    price: number | string,
    currency: SupportedCurrencies = 'EUR',
  ) =>
    getPriceWithCurrency({
      price: Number(price),
      currency,
      i18n: $i18n,
    })

  const setPreOrderFromCookie = (houseId: string) => {
    const cookieName = `${houseId}_pre_order`
    const keys = [
      'destinationId',
      'endDate',
      'nightCount',
      'pid',
      'startDate',
    ] as const

    const data = keys.reduce<Partial<Record<(typeof keys)[number], string>>>(
      (acc, key) => {
        const cookie = $httpCookies
          .get<string | number>(`${cookieName}_${key}`)
          .toString()
        return { ...acc, [key]: cookie }
      },
      {},
    )

    if (Object.values(data).some((d) => !d)) {
      throw new Error('Invalid cookies preOrder')
    } else {
      housePreOrderState.value = {
        ...housePreOrderState.value,
        ...data,
      }
    }

    return data
  }

  const setPreOrderAndCookies = (
    payload: Pick<
      HousePreOrder,
      'destinationId' | 'endDate' | 'nightCount' | 'pid' | 'price' | 'startDate'
    >,
  ) => {
    const cookieName = `${payload.pid}_pre_order`

    for (const [key, value] of Object.entries(payload)) {
      $httpCookies.set(`${cookieName}_${key}`, value)
    }

    housePreOrderState.value = {
      ...housePreOrderState.value,
      ...payload,
    }
  }

  const setExternalIdentifier = (externalIdentifier: string) => {
    housePreOrderState.value.externalIdentifier = externalIdentifier
  }

  const setRequestUuid = (value: string) => {
    housePreOrderState.value.uuid = value
  }

  const setPrice = (price: string) => {
    housePreOrderState.value.price = price
  }
  const setBookingSent = (id?: string | number) => {
    housePreOrderState.value.bookingSent = (id ?? '').toString()
  }

  return {
    deposit,
    fetchPriceCurrencyHouse,
    formatPrice,
    isEmpty,
    preOrder,
    setBookingSent,
    setExternalIdentifier,
    setPreOrderAndCookies,
    setPreOrderFromCookie,
    setPrice,
    setRequestUuid,
  }
}
